/**
 * LLM Dispatcher
 * Routes generation calls to the active provider's service.
 * Reads `llm_provider` from localStorage and dispatches accordingly.
 *
 * geminiService.ts is used directly for Gemini (untouched).
 * Claude, OpenAI, and xAI each have their own service file.
 */

import { HookhouseFormData, ParsedSunoOutput, MusicCritique, SunoClip } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { getProvider, getClaudeModel, getOpenAIModel, getXAIModel } from './llmShared';
import { applyLockedPersonaStyle } from '@/shared/personaLock';
import { applyVocalIdentityToStyle, isVocalSlotActive } from '@/shared/vocalIdentity';

import * as gemini from './geminiService';
import * as claude from './claudeService';
import * as openai from './openaiService';
import * as xai from './xaiService';

function getService() {
  const provider = getProvider();
  console.log(`🎛️ [LLM Dispatcher] Active provider: ${provider}`);
  switch (provider) {
    case 'claude': return claude;
    case 'openai': return openai;
    case 'xai': return xai;
    case 'gemini':
    default: return gemini;
  }
}

function getActiveModel(): string {
  const provider = getProvider();
  switch (provider) {
    case 'claude': return getClaudeModel();
    case 'openai': return getOpenAIModel();
    case 'xai': return getXAIModel();
    case 'gemini':
    default: return localStorage.getItem('gemini_model') || 'gemini-3-flash-preview';
  }
}

function stampOutput(result: ParsedSunoOutput): ParsedSunoOutput {
  result.llmProvider = getProvider();
  result.llmModel = getActiveModel();

  // Ensure charLimits are always present (covers all providers including Gemini)
  if (!result.charLimits) {
    const styleLen = (result.style || '').length;
    const excludeLen = (result.excludedStyle || '').length;
    const lyricsLen = (result.lyricsWithTags || '').length;
    result.charLimits = {
      styleTags: { count: styleLen, max: 1000, over: styleLen > 1000 },
      excludedStyles: { count: excludeLen, max: 1000, over: excludeLen > 1000 },
      lyrics: { count: lyricsLen, max: 5000, over: lyricsLen > 5000 },
    };
  }

  return result;
}

export const generateSunoPrompt = async (
  data: HookhouseFormData,
  vocalArchitecture?: string,
  productionArchitecture?: string,
  riffDnaArchitecture?: string,
  soloArchitecture?: string,
  narrativeArchitecture?: string,
  personaOverride?: PersonaOverride | null
): Promise<ParsedSunoOutput> => {
  const result = await getService().generateSunoPrompt(data, vocalArchitecture, productionArchitecture, riffDnaArchitecture, soloArchitecture, narrativeArchitecture, personaOverride);
  // Deterministic half of persona locking -- the ONLY field this can be a
  // hard guarantee for, since it's a single isolated string. See
  // src/shared/personaLock.ts. No-op when personaOverride isn't locked.
  // Vocal profile's own style/exclude tags go verbatim into a reserved slot at
  // the front of the Style/Exclude fields -- see src/shared/vocalIdentity.ts.
  // No-op when a persona or style preset overrides the vocal profile.
  const withVocal = applyVocalIdentityToStyle(result, vocalArchitecture, data, personaOverride);
  return stampOutput(applyLockedPersonaStyle(withVocal, personaOverride));
};

export const regenerateWithAdvice = async (
  originalOutput: ParsedSunoOutput,
  critique: MusicCritique,
  vocalArchitecture?: string,
  data?: HookhouseFormData,
  personaOverride?: PersonaOverride | null
): Promise<ParsedSunoOutput> => {
  // AUTO-048: only tell the architect the voice tags are pre-filled when the
  // slot below will actually fill them.
  const vocalTagsPrefilled = !!data && isVocalSlotActive(vocalArchitecture, data, personaOverride);
  const result = await getService().regenerateWithAdvice(originalOutput, critique, { vocalTagsPrefilled });
  // Same deterministic Style/Exclude post-processing as generateSunoPrompt
  // (AUTO-040) -- without it a regenerated take lost the vocal identity slot.
  const withVocal = data ? applyVocalIdentityToStyle(result, vocalArchitecture, data, personaOverride) : result;
  return stampOutput(applyLockedPersonaStyle(withVocal, personaOverride));
};

export const generateImageBlueprint = async (
  output: ParsedSunoOutput
): Promise<string> => {
  return getService().generateImageBlueprint(output);
};

export const generateMusicCritique = async (
  clip: SunoClip
): Promise<MusicCritique> => {
  return getService().generateMusicCritique(clip);
};
